import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageContainer from "../components/layout/PageContainer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ConfirmDialog from "../components/common/ConfirmDialog";
import Pagination from "../components/common/Pagination";
import ScanForm from "../components/scans/ScanForm";
import ScanResults from "../components/scans/ScanResults";
import ScanHistory from "../components/scans/ScanHistory";
import { useAccessibility } from "../hooks/useAccessibility";
import { useWebsite } from "../hooks/useWebsite";
import { websiteService } from "../services/api/websiteService";
import { wordPressService } from "../services/api/wordPressService";
import { shopifyService, SENTRYPRIME_URL } from "../services/api/shopifyService";

const Scans = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    scanUrl,
    isScanning,
    scanError,
    scanData,
    getScanHistory,
    getLatestScan,
  } = useAccessibility();
  const { getAllWebsites } = useWebsite();

  const [currentScan, setCurrentScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isApplyingFixes, setIsApplyingFixes] = useState(false);
  const [showApplyFixesConfirm, setShowApplyFixesConfirm] = useState(false);
  const [fixesToApply, setFixesToApply] = useState([]);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [isCheckingShopifyStatus, setIsCheckingShopifyStatus] = useState(false);
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page === 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  // Load scan history
  const loadScanHistory = async (page = 1) => {
    setIsLoadingHistory(true);
    try {
      const response = await getScanHistory({ limit: pagination.limit, page });

      // Handle API response structure: { success: true, data: [...], page, limit, totalPages, totalResults }
      const responseData = response?.data || response?.scans || [];
      setScanHistory(Array.isArray(responseData) ? responseData : []);

      // Update pagination state
      if (response) {
        const currentPage = response.page || page;
        const totalPages = response.totalPages || 1;
        const totalDocs =
          response.totalResults || response.count || responseData.length;

        setPagination((prev) => ({
          ...prev,
          page: currentPage,
          limit: response.limit || prev.limit,
          totalDocs: totalDocs,
          totalPages: totalPages,
          hasNextPage: currentPage < totalPages,
          hasPrevPage: currentPage > 1,
        }));
      }
    } catch (error) {
      console.error("Error loading scan history:", error);
      toast.error("Failed to load scan history");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Load scan history on mount and when pagination page changes
  useEffect(() => {
    loadScanHistory(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  // Handle scan submission
  const handleScanSubmit = (data) => {
    const scanPayload = {
      url: data.url,
      wcagLevel: data.wcagLevel,
    };

    // Add websiteConnectionId if provided
    if (data.websiteConnectionId) {
      scanPayload.websiteConnectionId = data.websiteConnectionId;
    }

    scanUrl(scanPayload, {
      onSuccess: (response) => {
        // API returns { success: true, data: { ... } }
        if (response.success && response.data) {
          setCurrentScan(response.data);
          toast.success("Scan completed successfully!");
          handlePageChange(1);
        } else if (response.data) {
          setCurrentScan(response.data);
          toast.success("Scan completed successfully!");
          handlePageChange(1);
        } else {
          toast.error("Scan completed but no results returned");
        }
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || error.message || "Scan failed";
        toast.error(errorMessage);
      },
    });
  };

  // Update current scan when scanData changes
  useEffect(() => {
    if (scanData) {
      if (scanData.success && scanData.data) {
        setCurrentScan(scanData.data);
      } else if (scanData.data) {
        setCurrentScan(scanData.data);
      } else if (scanData.scan) {
        setCurrentScan(scanData.scan);
      }
    }
  }, [scanData]);

  const violations = currentScan?.violations || [];

  const getWebsiteConnectionId = (scan) =>
    scan?.websiteConnectionId ||
    scan?.websiteConnection?._id ||
    scan?.websiteConnection?.id ||
    (scan?.websiteConnection && typeof scan.websiteConnection === "string"
      ? scan.websiteConnection
      : null);

  // Check the Shopify AI accessibility fixer app embed status whenever the
  // active scan belongs to a Shopify website connection
  useEffect(() => {
    const websiteConnectionId = getWebsiteConnectionId(currentScan);
    if (!websiteConnectionId) {
      setShopifyStatus(null);
      return;
    }

    let isCancelled = false;

    const checkShopifyStatus = async () => {
      setIsCheckingShopifyStatus(true);
      try {
        const websiteResponse =
          await websiteService.getWebsiteById(websiteConnectionId);
        const website =
          websiteResponse?.website || websiteResponse?.data || websiteResponse;

        if (!website || website.platform !== "shopify" || !website.siteUrl) {
          if (!isCancelled) setShopifyStatus(null);
          return;
        }

        const status = await shopifyService.getExtensionStatus(
          website.myshopifyDomain || website.siteUrl,
        );
        if (!isCancelled) {
          setShopifyStatus({ ...status, siteUrl: website.siteUrl });
        }
      } catch (error) {
        console.error("Error checking Shopify extension status:", error);
        if (!isCancelled) setShopifyStatus(null);
      } finally {
        if (!isCancelled) setIsCheckingShopifyStatus(false);
      }
    };

    checkShopifyStatus();

    return () => {
      isCancelled = true;
    };
  }, [currentScan]);

  const handleEnableShopifyExtension = () => {
    if (!shopifyStatus) return;
    const { shop, siteUrl, theme, block } = shopifyStatus;
    const shopDomain = shop || siteUrl;
    if (!shopDomain || !theme?.id || !block?.type) {
      window.open(
        `${SENTRYPRIME_URL}/authenticate?shop=${encodeURIComponent(shopDomain || "")}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    // block.type looks like: shopify://apps/<handle>/blocks/<block-handle>/<uuid>
    const parts = block.type.split("/");
    const uuid = parts[parts.length - 1];
    const blockHandle = parts[parts.length - 2];
    const activateAppId = `${uuid}/${blockHandle}`;

    window.open(
      `https://${shopDomain}/admin/themes/${theme.id}/editor?context=apps&activateAppId=${encodeURIComponent(activateAppId)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const applyAllFixes = async () => {
    if (!currentScan) return;

    // Check if scan has a websiteConnectionId - check multiple possible field names and locations
    const websiteConnectionId = getWebsiteConnectionId(currentScan);

    if (!websiteConnectionId) {
      toast.warning(
        "This scan is not associated with a website connection. Please scan from the Integrations page or Website Connections page to apply fixes.",
      );
      return;
    }

    // Verify the website connection exists and is WordPress
    try {
      // Fetch website connection by ID using the service directly
      const websiteResponse =
        await websiteService.getWebsiteById(websiteConnectionId);
      const website =
        websiteResponse?.website || websiteResponse?.data || websiteResponse;

      if (!website) {
        toast.error(
          "Website connection not found. Please check if the connection still exists.",
        );
        return;
      }

      // Check if it's a WordPress connection
      if (website.platform !== "wordpress") {
        toast.warning(
          `Auto-fixes are currently only available for WordPress connections. This connection is for ${website.platform || "unknown platform"}.`,
        );
        return;
      }

      // Check if connection is active
      if (website.isActive === false) {
        toast.warning(
          "This website connection is not active. Please activate it first.",
        );
        return;
      }

      const fixable = [
        "image-alt",
        "landmark-unique",
        "page-has-heading-one",
        "page-no-duplicate-h1",
      ];
      const fixesToApply = [];

      violations.forEach((violation) => {
        if (fixable.includes(violation.id)) {
          violation.nodes?.forEach((node) => {
            // Use API suggested fix if available, otherwise generate one
            if (violation.suggestedFix && violation.suggestedFix.length > 0) {
              fixesToApply.push({
                violationId: violation.id,
                target: node.target,
                currentHtml: node.html,
                fixedHtml: violation.suggestedFix[0],
              });
            } else if (violation.id === "image-alt" && node.html) {
              // Generate fix logic here (simplified)
              fixesToApply.push({
                violationId: violation.id,
                target: node.target,
                currentHtml: node.html,
                fixedHtml: node.html.replace(
                  /<img/i,
                  '<img alt="Descriptive text about this image"',
                ),
              });
            }
          });
        }
      });

      if (fixesToApply.length === 0) {
        toast.info("No auto-fixable violations found.");
        return;
      }

      setFixesToApply(fixesToApply);
      setShowApplyFixesConfirm(true);
    } catch (error) {
      console.error("Error fetching website connection:", error);
      toast.error("Failed to fetch website connection details.");
    }
  };

  const handleConfirmApplyFixes = async () => {
    setShowApplyFixesConfirm(false);
    const fixes = fixesToApply;
    setFixesToApply([]);

    if (!currentScan || fixes.length === 0) return;

    // Get websiteConnectionId from scan - check multiple possible field names
    const websiteConnectionId =
      currentScan.websiteConnectionId ||
      currentScan.websiteConnection?._id ||
      currentScan.websiteConnection?.id ||
      currentScan.websiteConnectionId?._id ||
      currentScan.websiteConnectionId?.id ||
      (typeof currentScan.websiteConnectionId === "string"
        ? currentScan.websiteConnectionId
        : null);

    if (!websiteConnectionId) {
      toast.error("Website connection ID not found in scan data.");
      setIsApplyingFixes(false);
      return;
    }

    setIsApplyingFixes(true);
    try {
      // Use the scan URL as pageUrl
      const response = await wordPressService.applyFixes(
        websiteConnectionId,
        fixes,
        null, // pageId (optional)
        currentScan.url, // pageUrl
      );

      if (response.success) {
        toast.success(
          `Successfully applied ${fixes.length} fixes to WordPress!`,
        );
        // Optionally reload the scan to see updated results
        // You might want to refetch the scan data here
      } else {
        toast.error(
          "Failed to apply fixes: " +
            (response.error || response.message || "Unknown error"),
        );
      }
    } catch (error) {
      console.error("Error applying fixes:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Unknown error";
      toast.error("Error applying fixes: " + errorMessage);
    } finally {
      setIsApplyingFixes(false);
    }
  };

  const exportReport = () => {
    if (!currentScan) return;

    const report = {
      url: currentScan.url,
      timestamp: currentScan.timestamp || new Date().toISOString(),
      scanResultId: currentScan.scanResultId,
      summary: currentScan.summary || {
        totalViolations: currentScan.violations?.length || 0,
        totalPasses: currentScan.passes?.length || 0,
        totalIncomplete: currentScan.incomplete?.length || 0,
        totalInapplicable: currentScan.inapplicable?.length || 0,
        complianceScore:
          currentScan.violations?.length === 0
            ? 100
            : Math.max(0, 100 - (currentScan.violations?.length || 0) * 5),
      },
      violations: currentScan.violations || [],
      passes: currentScan.passes || [],
      incomplete: currentScan.incomplete || [],
      inapplicable: currentScan.inapplicable || [],
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: "application/json",
    });
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `accessibility-report-${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleViewReport = async (scan) => {
    try {
      // Normalize scan object so ScanResults receives all expected fields
      const normalize = (s) => {
        if (!s) return s;
        const resultId = s.scanResultId || s.scanId || s.id || s._id || null;
        const summary = s.summary || {
          totalViolations: s.violations?.length || 0,
          totalPasses: s.passes?.length || 0,
          totalIncomplete: s.incomplete?.length || 0,
          totalInapplicable: s.inapplicable?.length || 0,
          complianceScore:
            s.violations?.length === 0
              ? 100
              : Math.max(0, 100 - (s.violations?.length || 0) * 5),
        };

        return {
          ...s,
          scanResultId: resultId,
          violations: s.violations || [],
          passes: s.passes || [],
          incomplete: s.incomplete || [],
          inapplicable: s.inapplicable || [],
          manualPasses: s.manualPasses || [],
          manualNotApplicable: s.manualNotApplicable || [],
          summary,
        };
      };

      // If the history item already contains results/summary, use it
      if (
        scan &&
        (scan.violations !== undefined ||
          scan.summary !== undefined ||
          scan.manualPasses !== undefined ||
          scan.manualNotApplicable !== undefined)
      ) {
        setCurrentScan(normalize(scan));
        toast.success("Scan details loaded");
        return;
      }

      // Otherwise, fetch latest scan by URL and normalize response
      const response = await getLatestScan(scan.url);
      const payload = response?.data || response?.scan || response || null;
      if (payload) {
        setCurrentScan(normalize(payload));
        toast.success("Scan details loaded");
      } else {
        toast.error("No scan data found");
      }
    } catch (error) {
      toast.error("Failed to load scan details");
      console.error(error);
    }
  };

  return (
    <PageContainer
      title="Accessibility Scans"
      subtitle="Manage and review your accessibility scans"
    >
      <ScanForm
        onSubmit={handleScanSubmit}
        isScanning={isScanning}
        scanError={scanError?.response?.data?.message || scanError?.message}
      />

      {isScanning && (
        <div className="mb-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <LoadingSpinner size="lg" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">
                  Scanning in Progress...
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Analyzing website for accessibility issues
                </p>
              </div>
            </div>
            <div className="mt-4 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full animate-pulse"
                style={{ width: "60%" }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {currentScan && (
        <ScanResults
          scan={currentScan}
          onApplyFixes={applyAllFixes}
          isApplyingFixes={isApplyingFixes}
          onExportReport={exportReport}
          shopifyStatus={shopifyStatus}
          isCheckingShopifyStatus={isCheckingShopifyStatus}
          onEnableShopifyExtension={handleEnableShopifyExtension}
        />
      )}

      <ScanHistory
        scanHistory={scanHistory}
        onViewReport={handleViewReport}
        isLoading={isLoadingHistory}
      />

      {/* Pagination */}
      {!isLoadingHistory && scanHistory.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalDocs={pagination.totalDocs}
          limit={pagination.limit}
          hasNextPage={pagination.hasNextPage}
          hasPrevPage={pagination.hasPrevPage}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmDialog
        isOpen={showApplyFixesConfirm}
        onClose={() => {
          setShowApplyFixesConfirm(false);
          setFixesToApply([]);
        }}
        onConfirm={handleConfirmApplyFixes}
        title="Apply Accessibility Fixes"
        message={`Apply ${fixesToApply.length} accessibility fixes to your WordPress site? This will modify your page content to fix accessibility issues (images, landmarks, etc.).`}
        variant="warning"
        confirmText="Apply Fixes"
        cancelText="Cancel"
        icon="mdi:auto-fix"
      />
    </PageContainer>
  );
};

export default Scans;
